/**
 * advance-ai.ts — per-tick AI opponents pass (M9 capstone).
 *
 * Runs after the other advance passes so the AI sees fully-updated state
 * each decision tick: resources already folded, research ticked, ships
 * spawned / despawned, structures promoted, combat resolved.
 *
 * Rate-limited to AI_TICK_INTERVAL: each non-player empire takes one
 * action per AI-tick (one structure build OR one ship queue OR one
 * research set OR one Constructor build) plus movement orders for all
 * idle ships. Keeps AI pacing visually calm and per-tick cost bounded.
 *
 * Per-race personalities come from `balance-ai.ts`:
 *   - RACE-AA Terran Consortium — turtle (economy / defence-first)
 *   - RACE-BB Kargath Imperium — rush (military-first, aggressive)
 *   - RACE-CC Reman Trust — hit-and-run (stealth, cloak-by-default,
 *     aggressive-at-range)
 *
 * All decisions flow through `applyCommand` so AI actions go through the
 * same validation + cost-deduction path as player actions. Determinism
 * is preserved — this pass reads state + writes through applyCommand,
 * no extra RNG draws.
 */

import { applyCommand } from './commands';
import {
  getAiConfig,
  type AiRaceConfig,
} from './balance-ai';
import { COLONISER_SPECIAL_ABILITY } from './balance-colonisation';
import { BIOME_XLSX_LABEL } from './balance-habitability';
import {
  CONSTRUCTOR_CHASSIS_BY_RACE,
  STATIONARY_SPECIAL_ABILITY,
  spaceBuildCostForKind,
  spaceSiteRequirementForKind,
} from './balance-space-structures';
import {
  buildCreditsForKind,
  buildMineralsForKind,
  isUniqueKind,
  maxLevelForKindByRace,
  maxStructuresForSize,
} from './balance-structures';
import { canBuildClassAtPlanet } from './planet-economy';
import { effectiveMaxHull, hyperdriveRangeMultiplier } from './research-effects';
import { firstStepTowards } from './pathfinding';
import {
  chooseDiplomaticTarget,
  decideOutcome,
  effectiveDiplomaticAggression,
  PICK_EPSILON,
} from './advance-ai-diplomacy';
import { diplomaticPerTickForEmpire } from './planet-economy';
import { nextFloat } from '../state/rng';
import type {
  Empire,
  EnvironmentEntity,
  GameState,
  Planet,
  ShipInstance,
  StructureKind,
  SystemId,
} from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';

/**
 * Run one AI tick for every non-player empire. Mutates state via commands.
 *
 * v3M7.1 — each empire's tickInterval is per-(difficulty) rather than a
 * single global. Hard-difficulty empires decide twice as often as Normal;
 * Easy half as often. We gate `state.tick % cfg.tickInterval` per empire
 * so different difficulties can coexist in the same game without one
 * suppressing the others.
 */
export function advanceAi(state: GameState): void {
  const empireIds = [...state.empires.keys()].sort((a, b) => a - b);
  for (const empireId of empireIds) {
    const empire = state.empires.get(empireId)!;
    if (empire.isPlayer) continue;
    // v3M21.1 — synthetic neutral empire is AI-inert: no economy actions,
    // no movement dispatch, no research. Filter early so the rest of the
    // pass doesn't have to defend against it.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    const config = getAiConfig(empire, state.aiBehaviours);
    if (config.tickInterval > 0 && state.tick % config.tickInterval !== 0) continue;
    // One economy action per AI-tick (priority-ordered).
    takeEconomyAction(state, empire, config);
    // v3M7.3c — retreat damaged combat ships first; the retreat order
    // overrides any engage / patrol moveOrder so a low-hull ship doesn't
    // sit dying in front of an enemy because it's already mid-engagement.
    executeRetreats(state, empire, config);
    // v3M7.3a — dispatch idle Colonisers before patrol/engage so the
    // moveOrder set by moveAndColonise gets respected by the move pass.
    dispatchIdleColonisers(state, empire, config);
    // v3M7.3b — dispatch idle Scouts on inter-system jumps for
    // exploration. Same ordering rule: setting moveOrder must happen
    // before issueMovementOrders so the patrol/engage pass skips them.
    dispatchScoutJumps(state, empire, config);
    // MFix7.5b — dispatch idle combat ships on inter-system jumps once
    // the empire is either Aggressive OR has at least one defensive
    // structure on its homeworld. Without this pass the AI never
    // projects force across system boundaries, so games on multi-system
    // maps stalemate at the home-system frontier.
    dispatchCombatJumps(state, empire, config);
    // MFix7.5a — dispatch idle Constructors to start a space-structure
    // build. Lifted out of the takeEconomyAction cascade so the pass
    // fires every AI-tick rather than only when the planet/ship/research
    // priorities all happen to fail in the same tick (which is rare in
    // early/mid-game and starves the AI of space structures).
    dispatchConstructorBuilds(state, empire, config);
    // v3M21.5.3 — diplomatic focus. AI picks a visible neutral to
    // invest Diplomatic resource in when its effective aggression is
    // above PICK_EPSILON.
    manageDiplomaticFocus(state, empire);
    // v3M21.5.4 — outcome invocation. If focus is set and a threshold
    // is unlocked, decide whether to invoke (or save for Annex if
    // aggressive + reachable in patience window).
    manageDiplomaticInvocation(state, empire);
    // Movement orders every AI-tick for idle ships.
    issueMovementOrders(state, empire, config);
  }
}

/**
 * v3M21.5.3 — set `empire.diplomaticFocus` if it's null and the
 * empire's effective diplomatic aggression is above `PICK_EPSILON`.
 * Walks visible neutrals via `findVisibleNeutrals`, scores each via
 * `scoreNeutral`, picks the highest score. The per-tick income
 * routing in `advance.ts` step 3a already auto-clears stale focus
 * (e.g. target was annexed last tick), so we only need to handle
 * focus === null here. Issues `setDiplomaticFocus` via `applyCommand`
 * so the validation chain (target is currently a neutral, etc.)
 * runs the same as a player-issued command.
 */
function manageDiplomaticFocus(state: GameState, empire: Empire): void {
  if (empire.diplomaticFocus !== null) return;
  if (effectiveDiplomaticAggression(state, empire) < PICK_EPSILON) return;
  const target = chooseDiplomaticTarget(state, empire);
  if (!target) return;
  applyCommand(state, {
    kind: 'setDiplomaticFocus',
    empireId: empire.id,
    planetId: target.id,
  });
}

/**
 * v3M21.5.4 — invoke a diplomatic outcome on the focus target if one
 * is unlocked and the AI's policy says now is the time. Pure-policy
 * decision delegated to `decideOutcome` (sees the pool, thresholds,
 * federation/trade state, aggression, and per-tick income); this
 * helper only handles the dispatch.
 */
function manageDiplomaticInvocation(state: GameState, empire: Empire): void {
  if (empire.diplomaticFocus === null) return;
  const target = state.planets.get(empire.diplomaticFocus);
  if (!target) return;
  const income = diplomaticPerTickForEmpire(state, empire.id);
  const decision = decideOutcome(state, empire, target, income);
  switch (decision) {
    case 'wait':
      return;
    case 'annex':
      applyCommand(state, {
        kind: 'invokeAnnex',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'vassal':
      applyCommand(state, {
        kind: 'invokeVassal',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'federation':
      applyCommand(state, {
        kind: 'invokeFederation',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'trade':
      applyCommand(state, {
        kind: 'invokeTrade',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
  }
}

// ---------------------------------------------------------------------------
// Economy: one action per AI-tick (priority-ordered)
// ---------------------------------------------------------------------------

/**
 * Walk the race's priority order and fire the first action that resolves.
 * Returns when an action lands or when all priorities exhaust. Priority
 * cascade (roughly): new planet structure → ship build → research →
 * structure upgrade. Constructor space-structure dispatch runs as its
 * own parallel pass (`dispatchConstructorBuilds`, MFix7.5a) so it's not
 * starved by the cascade — early/mid-game the upper priorities almost
 * always succeed, which previously left Constructors idle until very
 * late in the game.
 */
function takeEconomyAction(state: GameState, empire: Empire, config: AiRaceConfig): void {
  if (tryBuildNextStructure(state, empire, config)) return;
  if (tryBuildNextShip(state, empire, config)) return;
  if (tryStartNextResearch(state, empire, config)) return;
  tryUpgradeStructure(state, empire, config);
}

/**
 * Try to build the first structure kind in the race's priority list that
 * can land on one of the empire's planets right now. Uniqueness, slot
 * cap, and resources all delegate to the command applier — AI just
 * picks a candidate and fires buildStructure.
 */
function tryBuildNextStructure(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  for (const kind of config.structurePriority) {
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (!canBuildStructureHere(state, planet, empire, kind)) continue;
      applyCommand(state, {
        kind: 'buildStructure',
        empireId: empire.id,
        planetId,
        structureKind: kind,
      });
      return true;
    }
  }
  return false;
}

function canBuildStructureHere(
  state: GameState,
  planet: Planet,
  empire: Empire,
  kind: StructureKind,
): boolean {
  if (planet.ownerEmpireId !== empire.id) return false;
  if (planet.structureIds.length >= maxStructuresForSize(planet.size)) return false;
  const sd = state.structuresData;
  if (isUniqueKind(sd, kind)) {
    for (const id of planet.structureIds) {
      const s = state.structures.get(id);
      if (s && s.kind === kind) return false;
    }
  }
  if (empire.resources.credits < buildCreditsForKind(sd, kind)) return false;
  if (empire.resources.minerals < buildMineralsForKind(sd, kind)) return false;
  return true;
}

/**
 * Try to queue the next priority chassis at a planet with a capable
 * Shipyard. Walks the race's ship priority list, then the empire's
 * planets; on the first resolvable (shipyard class + resources + pop +
 * chassis tech gate) it fires buildShip.
 */
function tryBuildNextShip(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  // MFix6.1 — class-progression heuristic. The legacy implementation
  // walked the priority list and built the FIRST resolvable chassis,
  // which means a list with Coloniser / Engineer / Scout / Corvette at
  // the top causes the AI to spam Class 1 chassis forever once those
  // are affordable — even after Class 4-6 chassis become researched
  // and buildable. The fix: walk the whole priority list, track every
  // resolvable (chassis, planet) pair, and pick the one with the
  // highest classLevel. Priority-list order remains the tie-breaker so
  // role preference (a Cruiser before a Battleship of equal class)
  // still matters.
  let best: {
    chassis: string;
    planetId: number;
    classLevel: number;
    priority: number;
  } | null = null;

  for (let i = 0; i < config.shipPriority.length; i++) {
    const chassis = config.shipPriority[i]!;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${chassis}`);
    if (!tmpl) continue;
    // Chassis tech gate (M12.3) — Class 1 implicit, Class 2+ needs the tech.
    if (tmpl.classLevel >= 2) {
      const techName = `Class ${tmpl.classLevel} Starship Chassis`;
      if (!empire.researchedTechs.has(techName)) continue;
    }
    if (empire.resources.credits < tmpl.creditsBuildCost) continue;
    if (empire.resources.minerals < tmpl.mineralsBuildCost) continue;
    // Find the first capable planet for this chassis. Lowest planet id
    // wins for determinism; details of which planet are secondary to the
    // chassis selection itself.
    let chosenPlanetId: number | null = null;
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (planet.currentPopulation < tmpl.popBuildCost) continue;
      if (!canBuildClassAtPlanet(state, planetId, tmpl.classLevel)) continue;
      chosenPlanetId = planetId;
      break;
    }
    if (chosenPlanetId === null) continue;

    if (
      best === null ||
      tmpl.classLevel > best.classLevel ||
      (tmpl.classLevel === best.classLevel && i < best.priority)
    ) {
      best = {
        chassis,
        planetId: chosenPlanetId,
        classLevel: tmpl.classLevel,
        priority: i,
      };
    }
  }

  if (best === null) return false;
  applyCommand(state, {
    kind: 'buildShip',
    empireId: empire.id,
    planetId: best.planetId,
    chassis: best.chassis,
  });
  return true;
}

/**
 * If no active research, pick the first category in the race's priority
 * list with a researchable tech (all prereqs met, not already done,
 * race-gated correctly) and set it as the target.
 */
function tryStartNextResearch(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  if (empire.activeResearch !== null) return false;

  for (const category of config.researchCategoryPriority) {
    // Within a category, walk techs by ascending level so the AI fills
    // out the bottom of the ladder before reaching higher-cost rungs.
    // v4M5.1 — when the legacy category is missing from the live tech
    // tree (e.g. `'Hyperdrive Fuel Consumption'` was renamed to
    // `'Engines'` at v4M5.1), fall back to a tech-name-prefix match
    // derived from the legacy category. Keeps AI_Behaviours.xlsx stable
    // through the xlsx-side category rename without drifting AI
    // priorities.
    let pool = [...state.techs.values()].filter((t) => t.category === category);
    if (pool.length === 0) {
      const fallbackPrefix = LEGACY_CATEGORY_TO_NAME_PREFIX[category];
      if (fallbackPrefix !== undefined) {
        pool = [...state.techs.values()].filter((t) =>
          t.techName.startsWith(fallbackPrefix),
        );
      }
    }
    const candidates = pool
      .filter((t) => t.race === 'All' || t.race === empire.raceId)
      .filter((t) => !empire.researchedTechs.has(t.techName))
      .filter((t) =>
        t.prerequisites.every((p) => empire.researchedTechs.has(p)),
      )
      .sort((a, b) => {
        const al = a.level ?? Number.POSITIVE_INFINITY;
        const bl = b.level ?? Number.POSITIVE_INFINITY;
        return al - bl;
      });
    if (candidates.length === 0) continue;
    applyCommand(state, {
      kind: 'setResearchTarget',
      empireId: empire.id,
      techName: candidates[0]!.techName,
    });
    return true;
  }
  return false;
}

/**
 * v4M5.1 — back-compat map for AI research-priority category drift.
 *
 * The xlsx authoring side moved hyperdrive techs from the legacy
 * 'Hyperdrive Fuel Consumption' category into the unified 'Engines'
 * category (which now also houses Hyperdrive Speed + Range). AI
 * priority lists in `AI_Behaviours.xlsx` weren't updated at the same
 * time (re-authoring would lump Speed / Range / Efficiency together,
 * losing the fuel-cost specificity); this map lets the dispatch
 * filter techs by tech-name prefix when the category lookup misses.
 *
 * Add entries here when other categories rename in the xlsx. Each
 * entry's prefix should be canonical (i.e. matches the tech-name
 * format the live xlsx authors use).
 */
const LEGACY_CATEGORY_TO_NAME_PREFIX: Record<string, string> = {
  'Hyperdrive Fuel Consumption': 'Hyperdrive Efficiency',
};

/**
 * MFix7.5a — Dispatch idle Constructors to start a space-structure
 * build. Walks each idle Constructor (no current build + in-system + not
 * cloaked + not EMP-disabled) and the race's space-structure priority
 * order; fires `buildSpaceStructure` on the first (constructor × kind)
 * pair that resolves (catalogue cost present, empire affords it, valid
 * target available). For site-gated kinds (mining / refinery), targets
 * the nearest matching environmental entity in the Constructor's
 * system. For open-space kinds (Sensor Buoy, Weapons Platforms, Repair
 * Yard, Warp Gate, Minefield), targets a point near the Constructor.
 *
 * Per-tick cap: one build dispatch across all the empire's Constructors
 * per AI-tick (matches the pre-MFix7.5a behaviour when this lived inside
 * the takeEconomyAction cascade). Multiple Constructors get parallelism
 * across consecutive AI-ticks rather than within one. Determinism
 * preserved by walking constructors in id-ascending order.
 *
 * Cloaked-Constructor edge case (RACE-CC defaultCloak): we skip cloaked
 * constructors here because `applyBuildSpaceStructure` rejects cloaked
 * ships. A future v4M0 pass can issue a setCloak: false before
 * dispatching.
 */
function dispatchConstructorBuilds(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const constructors = [...state.ships.values()]
    .filter((s) => s.ownerEmpireId === empire.id)
    .filter((s) => s.chassis === constructorChassis)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.systemId !== null)
    .filter((s) => !s.cloaked)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);
  if (constructors.length === 0) return;

  const sd = state.structuresData;
  for (const constructor of constructors) {
    for (const kind of config.spaceStructurePriority) {
      const cost = spaceBuildCostForKind(sd, kind);
      if (cost.credits <= 0) continue; // missing catalogue entry
      if (empire.resources.credits < cost.credits) continue;
      if (empire.resources.minerals < cost.minerals) continue;

      const target = pickBuildTarget(state, constructor, kind);
      if (target === null) continue;

      applyCommand(state, {
        kind: 'buildSpaceStructure',
        empireId: empire.id,
        constructorShipId: constructor.id,
        structureKind: kind,
        sx: target.sx,
        sy: target.sy,
      });
      return;
    }
  }
}

/**
 * Find a suitable target (sx, sy) for the Constructor's build of `kind`.
 * For site-gated kinds, picks the centre of the nearest matching
 * environmental entity in the Constructor's system. For open-space
 * kinds, picks a point close to the Constructor (slight offset so the
 * structure doesn't overlap the Constructor itself).
 */
function pickBuildTarget(
  state: GameState,
  constructor: ShipInstance,
  kind: string,
): { sx: number; sy: number } | null {
  if (constructor.systemId === null) return null;
  const required = spaceSiteRequirementForKind(state.structuresData, kind);
  if (required !== 'open') {
    let best: EnvironmentEntity | null = null;
    let bestDistSq = Infinity;
    for (const env of state.environment.values()) {
      if (env.systemId !== constructor.systemId) continue;
      if (env.kind !== required) continue;
      const dx = env.sx - constructor.sx;
      const dy = env.sy - constructor.sy;
      const d = dx * dx + dy * dy;
      if (d < bestDistSq) {
        bestDistSq = d;
        best = env;
      }
    }
    return best ? { sx: best.sx, sy: best.sy } : null;
  }
  // Open-space kind — place near the Constructor but with a small
  // offset so the built structure doesn't land exactly on top of it.
  return {
    sx: clamp01(constructor.sx + 0.03),
    sy: clamp01(constructor.sy + 0.03),
  };
}

/**
 * Fallback economy action: find the lowest-level built structure in the
 * race's structure-priority list and upgrade it. Stops after one
 * successful upgrade so the AI doesn't drain its treasury on cascading
 * upgrades in a single tick.
 */
function tryUpgradeStructure(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  const sd = state.structuresData;
  for (const kind of config.structurePriority) {
    // Per-kind cost lookup — different kinds may have different costs in the
    // post-v3M14b catalogue. Early-out per kind rather than globally.
    if (empire.resources.credits < buildCreditsForKind(sd, kind)) continue;
    if (empire.resources.minerals < buildMineralsForKind(sd, kind)) continue;
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      for (const id of planet.structureIds) {
        const s = state.structures.get(id);
        if (!s || s.kind !== kind) continue;
        // MFix5.3 — race-gated cap (Embassy: RACE-AA L2, BB/CC L1).
        if (s.level < 1 || s.level >= maxLevelForKindByRace(sd, s.kind, empire.raceId)) continue;
        if (s.buildTicksRemaining > 0) continue;
        applyCommand(state, {
          kind: 'upgradeStructure',
          empireId: empire.id,
          structureId: s.id,
        });
        return true;
      }
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Coloniser dispatch (v3M7.3a)
// ---------------------------------------------------------------------------

/**
 * For each idle Coloniser, if `config.coloniserPriority > 0`, find the
 * nearest landable uninhabited planet in the same system and queue a
 * `moveAndColonise` order. Landability requires either Gaia (universal)
 * or the matching `Settlement: <Biome>` tech (matches the colonise
 * command's tech gate). Cross-system colonisation is deferred to v3M7.3b
 * (inter-system jumps); this pass only operates within the Coloniser's
 * current system.
 *
 * "Idle" means: in-system, no moveOrder, no pendingColoniseTargetId, no
 * constructionOrder, not EMP-disabled. The auto-colonise hook in
 * `advance.ts` (MFix3.4) takes over once the Coloniser arrives at the
 * planet.
 *
 * `coloniserPriority` is a 0..10 knob: 0 disables dispatch entirely
 * (Fortifier / Aggressive), >=1 dispatches all idle Colonisers per
 * AI-tick. Finer-grained throttling is a v3M10 balance concern.
 */
function dispatchIdleColonisers(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.coloniserPriority <= 0) return;

  const colonisers = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingColoniseTargetId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => {
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${s.chassis}`);
      return (
        tmpl !== undefined &&
        tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)
      );
    })
    .sort((a, b) => a.id - b.id);
  if (colonisers.length === 0) return;

  // Track planets we dispatch to in this pass so two Colonisers don't
  // both target the same planet on the same tick.
  const claimed = new Set<number>();
  // MFix6.1 — Track destination systems claimed for cross-system
  // dispatch so two Colonisers don't both jump to the same neighbour
  // looking for the same target on a single tick.
  const claimedSystems = new Set<SystemId>();

  for (const ship of colonisers) {
    // Same-system fast path — if a landable planet exists in the
    // Coloniser's current system, sail-and-colonise.
    const target = pickColoniseTarget(state, empire, ship, claimed);
    if (target !== null) {
      claimed.add(target.id);
      applyCommand(state, {
        kind: 'moveAndColonise',
        empireId: empire.id,
        shipId: ship.id,
        targetPlanetId: target.id,
      });
      continue;
    }

    // MFix6.1 — Cross-system fallback. The home system has no landable
    // planet (uninhabited Gaia or matching Settlement-Biome tech).
    // Walk the jump graph (single-hop) for the nearest linked system
    // that does. Issue moveAndJump toward the JP; on the next AI tick,
    // after the Coloniser arrives in the new system, this same
    // dispatcher will pick up the same-system path and finish the
    // colonise.
    const cross = pickCrossSystemColoniseTarget(
      state,
      empire,
      ship,
      claimedSystems,
    );
    if (cross !== null) {
      claimedSystems.add(cross.targetSystemId);
      // v4M5.3b2 — dispatch the new targetSystemId schema. Internally
      // resolved to a JP via `resolveJumpPointForCommand` (commands.ts).
      applyCommand(state, {
        kind: 'moveAndJumpShips',
        shipIds: [ship.id],
        targetSystemId: cross.targetSystemId,
        arrivalAngle: null,
      });
    }
  }
}

/**
 * Compute the single-ship effective hyperdrive range for an AI ship.
 * Same formula as `applyJumpShips`'s fleet-min calculation, scoped to
 * the one ship the AI is about to dispatch. Returns 0 if the chassis
 * template can't be resolved (caller treats as "ship can't jump").
 */
function shipHyperdriveRange(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): number {
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return 0;
  return tmpl.hyperdriveRange * hyperdriveRangeMultiplier(state, empire);
}

/**
 * MFix6.1 + v4M5.5 — Cross-system dispatch helper for
 * `dispatchIdleColonisers`. Walks every system in the galaxy looking
 * for one with a landable uninhabited planet for this empire (Gaia or
 * matching Settlement: <Biome> tech); for each candidate, asks the
 * pathfinder for the first range-respecting hop from the Coloniser's
 * current system. Returns the closest reachable candidate's first
 * step, or null when no path exists.
 *
 * v4M5.5 — `firstStepTowards` returns the candidate itself when
 * it's a direct neighbour in range, or an intermediate stepping-stone
 * when multi-hop pathing is needed. The AI re-evaluates each tick, so
 * the Coloniser walks the path one hop at a time and reaches the
 * landable system across multiple ticks.
 */
function pickCrossSystemColoniseTarget(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
  claimedSystems: ReadonlySet<SystemId>,
): { targetSystemId: SystemId } | null {
  if (ship.systemId === null) return null;
  const sourceSystemId = ship.systemId;
  const src = state.systems.get(sourceSystemId);
  if (!src) return null;
  const fleetRange = shipHyperdriveRange(state, empire, ship);
  if (fleetRange <= 0) return null;

  let best: {
    firstStep: SystemId;
    targetDistSq: number;
    targetSystemId: SystemId;
  } | null = null;

  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;
    if (claimedSystems.has(candidate.id)) continue;

    // Cheap landability scan over the candidate. The same-system
    // path on the next AI tick picks the actual planet after the
    // Coloniser arrives.
    let hasLandable = false;
    for (const planet of state.planets.values()) {
      if (planet.systemId !== candidate.id) continue;
      if (planet.ownerEmpireId !== null) continue;
      if (planet.currentPopulation > 0) continue;
      if (planet.biome !== 'gaia') {
        const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
        if (!empire.researchedTechs.has(`Settlement: ${xlsxBiome}`)) continue;
      }
      hasLandable = true;
      break;
    }
    if (!hasLandable) continue;

    const firstStep = firstStepTowards(
      state,
      sourceSystemId,
      candidate.id,
      fleetRange,
    );
    if (firstStep === null) continue;

    const dx = candidate.x - src.x;
    const dy = candidate.y - src.y;
    const distSq = dx * dx + dy * dy;
    if (
      best === null ||
      distSq < best.targetDistSq ||
      (distSq === best.targetDistSq && candidate.id < best.targetSystemId)
    ) {
      best = { firstStep, targetDistSq: distSq, targetSystemId: candidate.id };
    }
  }
  return best ? { targetSystemId: best.firstStep } : null;
}

/**
 * Pick the nearest landable uninhabited planet in the Coloniser's system
 * that the empire could legally colonise on arrival (Gaia or matching
 * Settlement: <Biome> tech). Tie-break by ascending planet id for
 * determinism. Returns null if no candidate exists.
 */
function pickColoniseTarget(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
  claimed: ReadonlySet<number>,
): Planet | null {
  if (ship.systemId === null) return null;
  let best: { planet: Planet; distSq: number } | null = null;
  for (const planet of state.planets.values()) {
    if (planet.systemId !== ship.systemId) continue;
    if (planet.ownerEmpireId !== null) continue;
    if (planet.currentPopulation > 0) continue;
    if (claimed.has(planet.id)) continue;
    if (planet.biome !== 'gaia') {
      const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
      if (!empire.researchedTechs.has(`Settlement: ${xlsxBiome}`)) continue;
    }
    const dx = planet.systemX - ship.sx;
    const dy = planet.systemY - ship.sy;
    const d = dx * dx + dy * dy;
    if (
      best === null ||
      d < best.distSq ||
      (d === best.distSq && planet.id < best.planet.id)
    ) {
      best = { planet, distSq: d };
    }
  }
  // Range is not gated here — moveAndColonise sets the move target to
  // the planet centre and `advancePendingColonisations` (advance.ts)
  // commits the colonise the moment the ship arrives in landing range.
  return best ? best.planet : null;
}

// ---------------------------------------------------------------------------
// Retreat (v3M7.3c)
// ---------------------------------------------------------------------------

/**
 * For each AI combat ship below `config.retreatHullThreshold`, override
 * its moveOrder with a retreat order. In the homeworld system the ship
 * heads back to the homeworld; in a foreign system it heads for the
 * nearest linked jump point (back-jump exit). Higher-quality "jump
 * specifically toward home" graph-routing is a v3M10 polish concern.
 *
 * Excluded from retreat:
 *   - Scouts — non-combat hull, jump-mission already; they keep their
 *     v3M7.3b orders.
 *   - Constructors / Colonisers — specialist intent supersedes retreat.
 *   - Ships with a pending strategic order (`pendingJumpSystemId`,
 *     `pendingColoniseTargetId`, `constructionOrder`) — already moving
 *     out of harm's way.
 *   - Mid-jump ships (`systemId === null`) — can't be ordered while
 *     in transit.
 *   - EMP-disabled ships — can't move regardless.
 *
 * Re-issuing the same retreat order each AI-tick is idempotent: the
 * move applier re-stamps the target. Cost is negligible at AI-tick
 * cadence.
 */
function executeRetreats(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.retreatHullThreshold <= 0) return;
  const homeworldPos = pickHomeworldPosition(state, empire);
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];

  const ships = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.pendingColoniseTargetId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.chassis !== SCOUT_CHASSIS)
    .filter((s) => s.chassis !== constructorChassis)
    .sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    if (tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) continue;

    const maxHull = effectiveMaxHull(state, empire, tmpl);
    if (maxHull <= 0) continue;
    const ratio = ship.currentHull / maxHull;
    if (ratio >= config.retreatHullThreshold) continue;

    if (homeworldPos !== null && ship.systemId === homeworldPos.systemId) {
      // Same system: flee toward homeworld surface position.
      applyCommand(state, {
        kind: 'moveShips',
        shipIds: [ship.id],
        targetX: homeworldPos.sx,
        targetY: homeworldPos.sy,
      });
      continue;
    }

    // Foreign system: head for the nearest in-range adjacent system
    // and bounce out. Retreat stays single-hop on purpose — a critical-
    // hull ship can't afford the multi-tick re-evaluation of a longer
    // path, and the v4M5.5 range gate skips unreachable neighbours.
    const dest = nearestAdjacentSystem(state, empire, ship);
    if (dest === null) continue;
    applyCommand(state, {
      kind: 'moveAndJumpShips',
      shipIds: [ship.id],
      targetSystemId: dest,
      arrivalAngle: null,
    });
  }
}

/**
 * Find the adjacent system whose galaxy-frame distance from the ship's
 * current system is smallest. Ties broken by ascending destination id.
 * Returns null if the ship is mid-jump or its system has no adjacencies.
 *
 * v4M5.3b3c — replaces the v3-era `nearestLinkedJumpPoint` helper.
 * Continuous Jump Area model has no JP entities; the system-pair
 * adjacency lives directly on `StarSystem.adjacentSystemIds`.
 */
function nearestAdjacentSystem(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): SystemId | null {
  if (ship.systemId === null) return null;
  const src = state.systems.get(ship.systemId);
  if (!src) return null;
  // v4M5.5 — range gate. A retreating ship beyond its hyperdrive
  // range to every neighbour can't actually bounce out; return null
  // so the caller falls through to in-system movement (or just sits
  // dying, but that's the player's range-investment problem).
  const fleetRange = shipHyperdriveRange(state, empire, ship);
  if (fleetRange <= 0) return null;
  const fleetRangeSq = fleetRange * fleetRange;
  let best: { destId: SystemId; distSq: number } | null = null;
  for (const destId of src.adjacentSystemIds) {
    const dst = state.systems.get(destId);
    if (!dst) continue;
    const dx = dst.x - src.x;
    const dy = dst.y - src.y;
    const d = dx * dx + dy * dy;
    if (d > fleetRangeSq) continue; // beyond retreat range
    if (
      best === null ||
      d < best.distSq ||
      (d === best.distSq && destId < best.destId)
    ) {
      best = { destId, distSq: d };
    }
  }
  return best ? best.destId : null;
}

// ---------------------------------------------------------------------------
// Inter-system scout jumps (v3M7.3b)
// ---------------------------------------------------------------------------

/**
 * Scout chassis identifier. v1 each race's Class-1 recon ship is named
 * literally "Scout" in the Ships.xlsx roster — kept as a named constant
 * so future renames stay grep-able. v3M7.3b only operates on this
 * chassis; the broader fleet stays in-system.
 */
const SCOUT_CHASSIS = 'Scout';

/**
 * Dispatch idle Scouts on exploration jumps when `jumpWillingness > 0`.
 *
 * "Idle" means: chassis === 'Scout', in-system, no moveOrder, no
 * pendingJumpSystemId, no constructionOrder, not EMP-disabled. For each
 * idle Scout we pick the jump point in its system whose linked system
 * the empire has the LEAST vision of (drives exploration toward unknown
 * frontiers). Dispatch runs through `moveAndJumpShips` so the existing
 * sail-then-jump pipeline handles fuel checks at JP arrival.
 *
 * Per-tick cap (v3M7.3b first cut): one dispatch per AI-tick. Higher
 * `jumpWillingness` will scale the cap in v3M10 balance — for now the
 * knob is purely on/off (>0 dispatches, ===0 stays home, matching
 * Fortifier behaviour). Determinism: scouts walked in id-ascending
 * order; ties between candidate destinations broken by ascending
 * systemId.
 */
function dispatchScoutJumps(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.jumpWillingness <= 0) return;

  const scouts = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.chassis === SCOUT_CHASSIS)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);
  if (scouts.length === 0) return;

  // First cut: one dispatch per AI-tick. Picking the lowest-id idle
  // scout keeps the choice deterministic.
  const scout = scouts[0]!;
  const target = pickScoutJumpDestination(state, empire, scout);
  if (target === null) return;

  // v4M5.3b3b — helper returns just destSystemId; appliers consume it
  // natively per `HYPERSPACE.md §4`.
  applyCommand(state, {
    kind: 'moveAndJumpShips',
    shipIds: [scout.id],
    targetSystemId: target.destSystemId,
    arrivalAngle: null,
  });
}

/**
 * v4M5.5 — Walk every system in the galaxy (not just neighbours) and
 * pick the lowest-visibility-count target the scout can reach
 * (single-hop or multi-hop). Returns the FIRST-STEP system to dispatch
 * the scout to; on subsequent AI ticks the scout re-evaluates and
 * continues stepping toward the destination. Range-respecting
 * pathfinding so the scout never tries to jump beyond its single-ship
 * hyperdriveRange in one hop.
 */
function pickScoutJumpDestination(
  state: GameState,
  empire: Empire,
  scout: ShipInstance,
): { destSystemId: number } | null {
  if (scout.systemId === null) return null;
  const sourceSystemId = scout.systemId;
  const fleetRange = shipHyperdriveRange(state, empire, scout);
  if (fleetRange <= 0) return null;

  const teamMap = state.teamVision.get(empire.id);
  function visibleShipsInSystem(systemId: number): number {
    if (!teamMap) return 0;
    let n = 0;
    for (const rec of teamMap.values()) {
      if (rec.lastSeenSystemId === systemId) n++;
    }
    return n;
  }

  let best: {
    firstStep: number;
    candidateId: number;
    score: number;
  } | null = null;
  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;
    const firstStep = firstStepTowards(
      state,
      sourceSystemId,
      candidate.id,
      fleetRange,
    );
    if (firstStep === null) continue;
    const score = visibleShipsInSystem(candidate.id);
    if (
      best === null ||
      score < best.score ||
      (score === best.score && candidate.id < best.candidateId)
    ) {
      best = { firstStep, candidateId: candidate.id, score };
    }
  }
  return best ? { destSystemId: best.firstStep } : null;
}

// ---------------------------------------------------------------------------
// Inter-system combat jumps (MFix7.5b)
// ---------------------------------------------------------------------------

/**
 * Planet-side structure kinds that count as "the home system has some
 * defences built" for MFix7.5b's combat-jump gate. A non-Aggressive
 * empire (Fortifier, Economist, Technologist, Expansionist) only starts
 * pushing combat ships through jump points once at least one of these
 * is up at L1+ on its homeworld — keeps turtles from naked-rushing
 * before they've established home defence, while still letting them
 * leave home eventually.
 */
const DEFENSIVE_PLANET_STRUCTURE_KINDS: ReadonlySet<StructureKind> = new Set<StructureKind>([
  'planetary-shields',
  'underground-bunkers',
  'energy-surface-battery',
  'kinetic-surface-battery',
  'orbital-defence-platform',
]);

/**
 * MFix7.5b — Dispatch one idle combat ship per AI-tick on an inter-
 * system jump. Closes the long-standing "AI never leaves home" gap that
 * pre-MFix7.5 left combat ships stuck patrolling the homeworld even
 * after the empire could clearly afford to project force.
 *
 * Gate (both must hold):
 *   1. `jumpWillingness > 0` — same on/off the scout-jump path uses.
 *   2. EITHER `aggressive === true` (Aggressive / Balanced behaviours)
 *      OR the empire's homeworld carries at least one Level 1+
 *      defensive planet structure (`DEFENSIVE_PLANET_STRUCTURE_KINDS`).
 *      Aggressive empires push out unconditionally; non-aggressive
 *      empires wait until the home is defended before sending combat
 *      ships abroad.
 *
 * Eligibility (per ship):
 *   - In-system, no moveOrder, no pendingJumpSystemId, no construction,
 *     not EMP-disabled, not cloaked.
 *   - Not a Scout (own dispatch path), not a Coloniser (own path), not
 *     a Constructor (own path), not Stationary (M14 space structures /
 *     ODP ring chassis — never jump).
 *
 * Per-tick cap: one dispatch per AI-tick (matches scout-jump cadence).
 * Lowest-id eligible ship wins — deterministic.
 *
 * Destination scoring: prefer the linked system with the most enemy
 * ships visible in this empire's team-vision (intel-driven). When no
 * destination has any enemy intel, falls through naturally to the
 * lowest linked-systemId neighbour (deterministic cold-start). The
 * existing sail-then-jump pipeline (`moveAndJumpShips`) handles fuel
 * checks at JP arrival so we don't repeat them here.
 *
 * Excluded by intent (deferred to v4M0): multi-hop pathing, fleet-
 * coordinated dispatch, threat-weighted target evaluation, recall on
 * home-defence emergency, RACE-CC cloaked-Constructor handling.
 */
function dispatchCombatJumps(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.jumpWillingness <= 0) return;
  if (!config.aggressive && !homeworldHasDefences(state, empire)) return;

  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const candidates = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => !s.cloaked)
    .filter((s) => s.chassis !== SCOUT_CHASSIS)
    .filter((s) => s.chassis !== constructorChassis)
    .filter((s) => {
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${s.chassis}`);
      if (!tmpl) return false;
      if (tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return false;
      if (tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) return false;
      return true;
    })
    .sort((a, b) => a.id - b.id);
  if (candidates.length === 0) return;

  const ship = candidates[0]!;
  const target = pickCombatJumpDestination(state, empire, ship);
  if (target === null) return;

  applyCommand(state, {
    kind: 'moveAndJumpShips',
    shipIds: [ship.id],
    targetSystemId: target.destSystemId,
    arrivalAngle: null,
  });
}

/**
 * Returns true when the empire's first owned planet (the homeworld for
 * the purposes of `pickHomeworldPosition` and the rest of advance-ai)
 * carries at least one fully-built (level >= 1) defensive structure.
 * Used by `dispatchCombatJumps` to gate non-Aggressive empires.
 */
function homeworldHasDefences(state: GameState, empire: Empire): boolean {
  const homeworldId = empire.planetIds[0];
  if (homeworldId === undefined) return false;
  const homeworld = state.planets.get(homeworldId);
  if (!homeworld) return false;
  for (const id of homeworld.structureIds) {
    const s = state.structures.get(id);
    if (!s) continue;
    if (s.level < 1) continue;
    if (DEFENSIVE_PLANET_STRUCTURE_KINDS.has(s.kind)) return true;
  }
  return false;
}

/**
 * v4M5.5 — Walk every system in the galaxy and pick the highest-enemy-
 * count target the ship can reach (single-hop or multi-hop). Returns
 * the FIRST-STEP system to dispatch to; the ship re-evaluates each
 * AI tick. Range-respecting so out-of-range targets stepping-stone
 * through intermediate systems. Ties broken by ascending candidate
 * systemId so cold-start (no intel anywhere) is deterministic.
 */
function pickCombatJumpDestination(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): { destSystemId: SystemId } | null {
  if (ship.systemId === null) return null;
  const sourceSystemId = ship.systemId;
  const fleetRange = shipHyperdriveRange(state, empire, ship);
  if (fleetRange <= 0) return null;

  const teamMap = state.teamVision.get(empire.id);
  function enemiesInSystem(systemId: SystemId): number {
    if (!teamMap) return 0;
    let n = 0;
    for (const rec of teamMap.values()) {
      if (rec.lastSeenSystemId === systemId) n++;
    }
    return n;
  }

  let best: {
    firstStep: SystemId;
    candidateId: SystemId;
    enemies: number;
  } | null = null;
  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;
    const firstStep = firstStepTowards(
      state,
      sourceSystemId,
      candidate.id,
      fleetRange,
    );
    if (firstStep === null) continue;
    const enemies = enemiesInSystem(candidate.id);
    if (
      best === null ||
      enemies > best.enemies ||
      (enemies === best.enemies && candidate.id < best.candidateId)
    ) {
      best = { firstStep, candidateId: candidate.id, enemies };
    }
  }
  return best ? { destSystemId: best.firstStep } : null;
}

// ---------------------------------------------------------------------------
// Movement: orders for idle ships every AI-tick
// ---------------------------------------------------------------------------

/**
 * For every idle AI ship, issue an appropriate move order. Idle =
 * in-system, no moveOrder, no active construction, no active jump, not
 * EMP-disabled. Three paths:
 *
 *   - Constructors: nothing (Constructor build command sets the order).
 *   - Aggressive races with visible enemies in same system: close with
 *     nearest visible enemy target.
 *   - Otherwise: random patrol point within AI_PATROL_RADIUS of the
 *     empire's homeworld (first owned planet).
 */
function issueMovementOrders(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const homeworldPos = pickHomeworldPosition(state, empire);
  const ships = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    // Constructors handled by the Constructor-build economy step.
    if (ship.chassis === constructorChassis) continue;
    // Colonisers handled by dispatchIdleColonisers (v3M7.3a) — they
    // shouldn't patrol or chase enemies. If a Coloniser is idle here it
    // means dispatch already passed without finding a target this tick.
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (tmpl?.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) continue;

    let target: { sx: number; sy: number } | null = null;
    if (config.aggressive) {
      target = findNearestVisibleEnemyPosition(state, empire, ship);
    }
    if (target === null && homeworldPos && ship.systemId === homeworldPos.systemId) {
      target = randomPatrolPoint(state, homeworldPos.sx, homeworldPos.sy);
    }
    if (target === null) continue;

    applyCommand(state, {
      kind: 'moveShips',
      shipIds: [ship.id],
      targetX: target.sx,
      targetY: target.sy,
    });
  }
}

/**
 * First owned planet's (systemId, systemX, systemY) — serves as the AI
 * empire's "home" for patrol + retreat. Null if the empire somehow has
 * no planets (e.g. mid-conquest, once conquest lands).
 */
function pickHomeworldPosition(
  state: GameState,
  empire: Empire,
): { systemId: SystemId; sx: number; sy: number } | null {
  for (const planetId of empire.planetIds) {
    const planet = state.planets.get(planetId);
    if (!planet) continue;
    return { systemId: planet.systemId, sx: planet.systemX, sy: planet.systemY };
  }
  return null;
}

function randomPatrolPoint(
  state: GameState,
  cx: number,
  cy: number,
): { sx: number; sy: number } {
  // Use the shared RNG so patrol positions are deterministic under seed.
  const a = nextFloat(state.rng) * Math.PI * 2;
  const r = nextFloat(state.rng) * state.balanceData.ai.patrolRadius;
  return {
    sx: clamp01(cx + Math.cos(a) * r),
    sy: clamp01(cy + Math.sin(a) * r),
  };
}

/**
 * Look up the empire's team-vision map, find the nearest enemy ship in
 * the same system as `ship`, return its position. Returns null if no
 * visible enemy. Respects teamVision's `seenVia` channel — ghost
 * records (seenVia null) don't count as targets to chase (same rule as
 * pickTarget in combat).
 */
function findNearestVisibleEnemyPosition(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): { sx: number; sy: number } | null {
  if (ship.systemId === null) return null;
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return null;
  let best: { sx: number; sy: number; distSq: number } | null = null;
  for (const rec of teamMap.values()) {
    if (rec.seenVia === null) continue;
    if (rec.lastSeenSystemId !== ship.systemId) continue;
    const target = state.ships.get(rec.shipId);
    if (!target || target.currentHull <= 0 || target.systemId === null) continue;
    const dx = target.sx - ship.sx;
    const dy = target.sy - ship.sy;
    const d = dx * dx + dy * dy;
    if (best === null || d < best.distSq) {
      best = { sx: target.sx, sy: target.sy, distSq: d };
    }
  }
  return best ? { sx: best.sx, sy: best.sy } : null;
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
